import React, { useState } from 'react';
import Header from './Header';
import YouTubeAnalyzer from './YouTubeAnalyzer';
import SunoCreator from './SunoCreator';
import SettingsModal from './SettingsModal';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const { FiSettings, FiSearch, FiEdit3, FiArrowLeft } = FiIcons;

const MainApp = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('creator');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('sunosync_settings');
    return saved ? JSON.parse(saved) : { apiKey: '', model: 'openai/gpt-4o-mini' };
  });

  const handleSaveSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem('sunosync_settings', JSON.stringify(newSettings));
    setIsSettingsOpen(false);
  };

  return (
    <>
      {/* Navigation Controls */}
      <div className="fixed top-6 left-6 z-40">
        <button 
          onClick={() => navigate('/')}
          className="p-3 bg-white/80 backdrop-blur-md rounded-2xl shadow-sm border border-orange-100 hover:border-orange-400 transition-all text-stone-600 hover:text-orange-600 flex items-center gap-2 font-medium"
        >
          <SafeIcon icon={FiArrowLeft} />
          <span className="hidden sm:inline">Home</span>
        </button>
      </div>

      <div className="fixed top-6 right-6 z-40">
        <button 
          onClick={() => setIsSettingsOpen(true)}
          className="p-3 bg-white/80 backdrop-blur-md rounded-2xl shadow-sm border border-orange-100 hover:border-orange-400 transition-all text-stone-600 hover:text-orange-600 flex items-center gap-2 font-medium"
        >
          <SafeIcon icon={FiSettings} className={isSettingsOpen ? 'rotate-90 transition-transform' : 'transition-transform'} />
          <span className="hidden sm:inline">Settings</span>
        </button>
      </div>

      <main className="relative z-10 container mx-auto px-4 py-16 flex flex-col items-center flex-grow">
        <Header />
        
        <div className="flex bg-white/60 backdrop-blur-sm p-1.5 rounded-2xl shadow-sm border border-orange-100 mb-12">
          <button 
            onClick={() => setActiveTab('creator')}
            className={`px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 transition-all ${
              activeTab === 'creator' 
                ? 'bg-orange-500 text-white shadow-md' 
                : 'text-stone-500 hover:text-orange-600 hover:bg-orange-50'
            }`}
          >
            <SafeIcon icon={FiEdit3} /> Prompt Creator
          </button>
          <button 
            onClick={() => setActiveTab('analyzer')}
            className={`px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 transition-all ${
              activeTab === 'analyzer' 
                ? 'bg-orange-500 text-white shadow-md' 
                : 'text-stone-500 hover:text-orange-600 hover:bg-orange-50'
            }`}
          >
            <SafeIcon icon={FiSearch} /> Reference Analyzer
          </button>
        </div>

        <div className="w-full relative min-h-[400px]">
          <AnimatePresence mode="wait">
            {activeTab === 'creator' ? (
              <motion.div
                key="creator"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
                className="w-full flex justify-center"
              >
                <SunoCreator 
                  settings={settings} 
                  onRequireSettings={() => setIsSettingsOpen(true)} 
                />
              </motion.div>
            ) : (
              <motion.div
                key="analyzer"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="w-full flex justify-center"
              >
                <YouTubeAnalyzer 
                  settings={settings} 
                  onRequireSettings={() => setIsSettingsOpen(true)} 
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      <SettingsModal 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)}
        onSave={handleSaveSettings}
        initialSettings={settings}
      />

      <footer className="relative z-10 text-center py-8 text-stone-500 text-sm mt-auto">
        <div className="flex items-center justify-center gap-6 mb-2">
          <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-green-500" /> Live Engine</span>
          <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-orange-500" /> Suno V5.5 Optimized</span>
        </div>
        <p>© {new Date().getFullYear()} SunoSync Pro • Powered by OpenRouter</p>
      </footer>
    </>
  );
};

export default MainApp;